import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import AddButton from "../../components/buttons/button";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import errorToast from "../../components/helper-components/error-toast";
import { Form, Formik } from "formik";
import {
  purchaseCreateAction,
  loadMinQtyItemsAction,
} from "../../redux/actions/purchase-action";
import {
  customerListAction,
  storeIdAction,
} from "../../redux/actions/general-action";
import SearchAddFeild from "../../components/forms/search-add-field";
import CloseIcon from "../../assets/icons/close-icon";
import AddressSidebar from "../../components/common-components/address-bar";
import { MapPin } from "lucide-react";
import { storeDetailsAction } from "../../redux/actions/invoices-action";
import BulkBillingTable from "../../components/purchase-section/bulk/bulk-billing-table";
import BulkReturnFooter from "../../components/purchase-section/bulk/bulk-return-footer";
import BulkPoAllocateSidePanel from "../../components/purchase-section/bulk/bulk-po-allocate-side-panel";
import BulkPoCreatingModal from "../../components/purchase-section/bulk/bulk-po-creating-modal";
import LoadingSpinner from "../../components/helper-components/loading-spinner";
import successToast from "../../components/helper-components/success-toast";
import { useNavigate } from "react-router-dom";
import {
  buildPoVendorAllocationGroups,
  buildUomPackDescription,
  convertBaseQtyToUom,
  getRowAllocations,
  getUomBaseFactor,
  groupItemDataByVendorShip,
} from "../../utils/purchase-order-calculations";

// How many items one "Load MIN Quantity Items" click fetches. The endpoint is
// called once per click (page 1 only); later pages are not requested.
const BULK_PO_PAGE_SIZE = 200;

// Which vendor (+ its ship-to address) pre-fills a freshly loaded row is the
// checkbox picked at load time — "preferred_vendor" reads the item's
// prefered_* fields, anything else reads its bh_vendore_*/bh_vendor_* fields,
// either already carrying a full address object from the API. Best Margin
// Vendor is the default, so a load with neither box ticked still prefills
// bh_vendore_name rather than leaving the vendor columns blank.
const buildVendorPrefill = (item, preference) => {
  if (preference === "preferred_vendor") {
    return {
      cmr_id: item?.prefered_vendor_id || "",
      cmr_code: item?.prefered_vendor_code || "",
      cmr_name: item?.prefered_vendor_name || "",
      cmr_ship_to_address: item?.prefered_ship_address || null,
      cmr_pay_to_address: item?.prefered_pay_address || null,
    };
  }
  return {
    cmr_id: item?.bh_vendore_id || "",
    cmr_code: item?.bh_vendore_code || "",
    cmr_name: item?.bh_vendore_name || "",
    cmr_ship_to_address: item?.bh_vendor_ship_address || null,
    cmr_pay_to_address: item?.bh_vendor_pay_address || null,
  };
};

// Row property order must match bulk-billing-table.js's column order (S No,
// Product Name, Item Code, Pack Size/UOM, Min Qty, Max Qty, Quantity, Vendor
// Code, Vendor Name, Unit Price, Total) — its cells render by object-key
// position, not by name (it renders Object.values(row) while colIndex < 11).
// Anything added below must therefore stay AFTER item_total_amount, which is
// the last rendered cell.
const buildRowFromItem = (item, preference, headerVendor = null) => {
  // A header vendor, when one was picked before loading, overrides the
  // Preferred / Best Margin prefill for every loaded row.
  const vendorPrefill = headerVendor?.cmr_id
    ? headerVendor
    : buildVendorPrefill(item, preference);
  const primaryUom =
    item?.uom_dropdown?.find((uom) => uom?.pack_description === "primary") ||
    item?.uom_dropdown?.[0];
  // Everywhere else in this codebase item_quantity is expressed in the
  // SELECTED uom and has to be multiplied by that uom's base_quantity to reach
  // base units — the regular PO screen does exactly that before comparing
  // against item_maximum_purchasing, and purchase-return-calculations.js
  // applies the same rule when pricing a batch.
  //
  // load-min-items returns the suggestion in BASE units: in a captured
  // response, suggested_net_reorder_qty (3209301) is exactly
  // item_maximum_purchasing (3232321) - total_quantity (23000) -
  // pending_order_qty (20), and item_maximum_purchasing is base-unit. The row
  // below selects the item's primary PACK, so handing that base-unit number
  // straight to item_quantity over-orders by the pack size — with a "Bag pack
  // of 10" it used to order 3209301 bags (32,093,010 pieces) instead of the
  // 320931 bags that actually cover the requirement.
  const requirementInBaseUnits = Number(item?.suggested_net_reorder_qty) || 0;
  // Pack size of the selected uom; 1 for any uom carrying no pack maths, so
  // single-unit items are completely unaffected by this conversion.
  const uomFactor = getUomBaseFactor(primaryUom);
  const quantity = convertBaseQtyToUom(requirementInBaseUnits, primaryUom);
  // The "add PO" endpoint's itemData carries a free-text pack description
  // (e.g. "1 * Piece") under this field name — load-min-items doesn't return
  // one directly, so it's derived from the item's primary UOM.
  const babyBoxPacking = buildUomPackDescription(primaryUom);

  return {
    sl_no: 1,
    item_name: item?.item_name,
    item_code: item?.item_code,
    item_uom: primaryUom?.alt_uom || "",
    item_minimun_purchasing: item?.item_minimun_purchasing,
    item_maximum_purchasing: item?.item_maximum_purchasing,
    item_quantity: quantity,
    cmr_code: vendorPrefill?.cmr_code || "",
    cmr_first_name: vendorPrefill?.cmr_name || "",
    item_unit_price: 0,
    item_total_amount: 0,
    // --- nothing below this line is rendered as a column ---
    item_id: item?.item_id,
    cmr_id: vendorPrefill?.cmr_id || "",
    item_generic_name: "",
    item_manufacturer: item?.item_manufacturer,
    item_manufacturer_name: item?.item_manufacturer_name || "",
    item_group: "",
    item_type: "",
    item_categeory: item?.item_categeory,
    item_uom_id: primaryUom?.item_id || "",
    item_baby_box_packing: babyBoxPacking,
    // Base units per one unit of the currently selected UOM — the same field
    // name and role the regular PO screen uses, so a base-unit comparison
    // reads the same number on both screens.
    selected_uom_qty: uomFactor,
    // The requirement in BASE units, kept so changing the UOM can re-derive
    // item_quantity instead of silently reinterpreting the old number in a
    // new unit.
    item_quantity_base: requirementInBaseUnits,
    // The untouched API value, retained so nothing from load-min-items is lost.
    suggested_net_reorder_qty: requirementInBaseUnits,
    uom_dropdown: item?.uom_dropdown
      ? item.uom_dropdown.map((uom) => ({
          // The three existing keys are unchanged — SelectField matches on
          // value/label and the table reads item_uom_id.
          item_uom_id: uom?.item_id,
          label: uom?.alt_uom,
          value: uom?.alt_uom,
          // Conversion factors kept on every option so switching UOM in the
          // grid can convert. `qty` mirrors the regular PO screen's option
          // shape exactly.
          qty: uom?.base_quantity,
          base_quantity: uom?.base_quantity,
          alt_quantity: uom?.alt_quantity,
          pack_description: uom?.pack_description,
          base_uom_name: uom?.base_uom?.name || uom?.sub_uom || "",
        }))
      : [],
    // buildVendorPrefill always returns an object now, so the allocation hangs
    // off an actual vendor id — an item the API has no vendor for still loads
    // with an empty vendor column instead of a blank-vendor allocation.
    vendor_allocations: vendorPrefill?.cmr_id
      ? [
          {
            key: `${item?.item_id}-alloc-0`,
            cmr_id: vendorPrefill.cmr_id,
            cmr_code: vendorPrefill.cmr_code,
            cmr_name: vendorPrefill.cmr_name,
            cmr_ship_to_address: vendorPrefill.cmr_ship_to_address,
            cmr_pay_to_address: vendorPrefill.cmr_pay_to_address,
            // Must stay identical to item_quantity — handleSubmit sends the
            // ALLOCATION's qty, not the row's.
            qty: quantity,
            is_preference: true,
          },
        ]
      : [],
  };
};

// Header vendor -> one line. Same fields the line's own Vendor cell and the
// Allocate panel write: the flat cmr_* columns plus a single allocation that
// takes the whole quantity (any earlier split is replaced).
const applyVendorToRow = (row, vendor) => ({
  ...row,
  cmr_id: vendor.cmr_id,
  cmr_code: vendor.cmr_code,
  cmr_first_name: vendor.cmr_name,
  vendor_allocations: [
    {
      key: `${row?.item_id}-alloc-0`,
      cmr_id: vendor.cmr_id,
      cmr_code: vendor.cmr_code,
      cmr_name: vendor.cmr_name,
      cmr_ship_to_address: vendor.cmr_ship_to_address || null,
      cmr_pay_to_address: vendor.cmr_pay_to_address || null,
      qty: Number(row?.item_quantity) || 0,
      is_preference: true,
    },
  ],
});

// Scratch Formik field name: AddressSidebar reads/writes
// `${name}_ship_to_address` / `${name}_pay_to_address` by a flat name.
const HEADER_ADDRESS_NAME = "bulk_po_header_vendor_addr";

// Stable identity for one (vendor, ship-to address) document — the same pair
// groupItemDataByVendorShip groups by, plus the pay-to address (which decides
// pot_pay_to_address / cmr_gstin / gst_type) and a sorted fingerprint of the
// lines it orders. A create run that dies half way records the key of every
// document the server confirmed, so pressing "Create Purchase Orders" again
// re-sends only what never went through. A raw index would be wrong:
// handleSubmit rebuilds `documents` from the current form values on every
// submit, so document N of the retry need not be document N of the first
// attempt. Lines are sorted because row order inside a group follows form-row
// order, which is not guaranteed stable across submits.
const buildDocumentKey = (docItems = []) => {
  const head = docItems[0] || {};
  const vendorId = head?.cmr_id || "";
  const shipId = head?.cmr_ship_to_address?.cmr_ship_address_id || "";
  const payId = head?.cmr_pay_to_address?.cmr_pay_address_id || "";
  const lines = docItems
    .map(
      (it) =>
        `${it?.item_id || ""}:${it?.item_uom_id || ""}:${
          Number(it?.item_quantity) || 0
        }:${Number(it?.item_unit_price) || 0}`
    )
    .sort()
    .join("|");
  return `${vendorId}__${shipId}__${payId}__${lines}`;
};

const BulkPurchaseOrder = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formikRef = useRef(null);

  const [heightOfFirstDiv, setHeightOfFirstDiv] = useState(0);
  const [heightOfSecondDiv, setHeightOfSecondDiv] = useState(0);
  const [loadMinQtyLoading, setLoadMinQtyLoading] = useState(false);
  // Bumped on every new load and on "Clear all" so a response from an older,
  // superseded load never overwrites the current rows. Deliberately NOT
  // bumped by an effect cleanup: React Fast Refresh re-runs effects while
  // keeping state, so a cleanup-bump there would discard the in-flight
  // response and leave loadMinQtyLoading stuck on true forever.
  const loadIdRef = useRef(0);
  const isMountedRef = useRef(false);
  const [vendorPreference, setVendorPreference] = useState("bh_vendor"); // "bh_vendor" (default) | "preferred_vendor" | null
  const [loadedPreference, setLoadedPreference] = useState("bh_vendor"); // what the CURRENT rows were loaded with
  // Header vendor: picked once, applied to every line; lines stay editable.
  // headerVendorRef is read when a load finishes (async).
  const [headerVendor, setHeaderVendor] = useState(null);
  const headerVendorRef = useRef(null);
  const [headerAddressOpen, setHeaderAddressOpen] = useState(false);
  const setHeaderVendorBoth = (vendor) => {
    headerVendorRef.current = vendor;
    setHeaderVendor(vendor);
  };
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [allocateRowIndex, setAllocateRowIndex] = useState(null);
  const [poCreateProgress, setPoCreateProgress] = useState({
    open: false,
    current: 0,
    total: 0,
  });
  // State updates from a fast double-click can land after both clicks have
  // already run handleSubmit — a ref flips synchronously, so the second
  // click is blocked immediately instead of racing the first render.
  const isCreatingRef = useRef(false);
  // Documents the server has already confirmed during THIS submission run.
  // A ref, not state: the create loop recurses from inside its own success
  // callback, so a state value read there would still be the one captured at
  // submit time (document 1's write would be invisible to document 2), and the
  // memory also has to survive every re-render setPoCreateProgress causes.
  const createdDocKeysRef = useRef(new Set());
  const [storeId, setStoreId] = useState("");
  const [storeState, setStoreState] = useState("");

  useEffect(() => {
    dispatch(storeIdAction((data) => setStoreId(data?.data?.store_id), () => {}));
    dispatch(
      storeDetailsAction(
        (data) => setStoreState((data?.data?.store_state || "").toLowerCase().trim()),
        () => {}
      )
    );
  }, [dispatch]);

  useEffect(() => {
    // .second-div (the footer) isn't rendered while the loading spinner is
    // up, so a resize during a load must not assume it exists — and the
    // heights are re-measured once loading flips, when it comes back.
    const updateHeight = () => {
      const firstDiv = document.querySelector(".first-div");
      const secondDiv = document.querySelector(".second-div");
      if (firstDiv) setHeightOfFirstDiv(firstDiv.clientHeight);
      if (secondDiv) setHeightOfSecondDiv(secondDiv.clientHeight);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, [loadMinQtyLoading]);

  // A response arriving after the screen is left is ignored.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const validationSchema = Yup.object().shape({});

  const initialData = {
    sl_no: 1,
    item_name: "",
    item_code: "",
    item_uom: "",
    item_minimun_purchasing: "",
    item_maximum_purchasing: "",
    item_quantity: 0,
    cmr_code: "",
    cmr_first_name: "",
    item_unit_price: 0,
    item_total_amount: 0,
    item_id: "",
    cmr_id: "",
    item_generic_name: "",
    item_manufacturer: "",
    item_group: "",
    item_type: "",
    item_categeory: "",
  };
  // Read by Formik once, at mount — loaded rows are written straight into the
  // live form (see loadMinQtyFn), so nothing here has to be rebuilt or
  // deep-compared on every render.
  const initialFormValues = { data: [initialData] };

  // Same vendor search component and loader as the line Vendor cell
  // (bulk-billing-table.js fetchVendorData).
  const fetchHeaderVendorData = (inputValue, callback, field) => {
    if (!inputValue?.length) return;
    dispatch(
      customerListAction(
        { field, value: inputValue, isVendor: true },
        (data) =>
          callback(
            data?.data === null
              ? []
              : (data?.data || []).map((item) => ({
                  value: item.cmr_id,
                  label: item[field],
                  cmr_first_name: item.cmr_first_name,
                  cmr_code: item?.cmr_code,
                }))
          ),
        () => callback([])
      )
    );
  };

  // Same pattern as the Bulk Purchase Return screen: ONE request, and the
  // rows go on screen (and the button stops loading) as soon as it returns.
  // Only the first BULK_PO_PAGE_SIZE items are loaded — no further pages.
  function loadMinQtyFn() {
    loadIdRef.current += 1;
    const loadId = loadIdRef.current;
    const preference = vendorPreference;
    setLoadMinQtyLoading(true);
    dispatch(
      loadMinQtyItemsAction(
        { pageNumber: 1, pageSize: BULK_PO_PAGE_SIZE },
        (res) => {
          if (!isMountedRef.current || loadId !== loadIdRef.current) return;
          setLoadMinQtyLoading(false);
          const items = res?.data?.items || [];
          setLoadedPreference(preference);
          // Freshly loaded rows replace whatever was on screen, so no document
          // created from the old rows can still match one built from the new ones.
          createdDocKeysRef.current = new Set();
          formikRef.current?.setFieldValue("data", [
            // headerVendorRef, not headerVendor: the vendor picked in the
            // header by the time the response lands, not at click time.
            ...items.map((item) =>
              buildRowFromItem(item, preference, headerVendorRef.current)
            ),
            initialData,
          ]);
          if (items.length === 0) {
            errorToast("No items with below minimum qty");
          }
        },
        (err) => {
          if (!isMountedRef.current || loadId !== loadIdRef.current) return;
          setLoadMinQtyLoading(false);
          errorToast(err?.data?.message || "Failed to load MIN quantity items");
        }
      )
    );
  }

  const handleSubmit = (values) => {
    if (isCreatingRef.current) return;

    const rows = values?.data?.slice(0, -1) || [];

    const missingUOM = rows.some((item) => !item.item_uom);

    const hasInvalidQuantity = rows.some(
      (item) => parseInt(item.item_quantity) === 0 || item.item_quantity === ""
    );

    const hasMissingVendor = rows.some((item) => {
      const allocations = getRowAllocations(item);
      return allocations.length === 0 || allocations.some((a) => !a.cmr_id);
    });

    // A split row's allocations must add up to exactly the item's required
    // quantity — otherwise the purchase order would under/over order it.
    const unbalanced = rows.find((row) => {
      const allocations = row.vendor_allocations || [];
      if (allocations.length < 2) return false;
      const sum = allocations.reduce((s, a) => s + (Number(a.qty) || 0), 0);
      return sum !== Number(row.item_quantity);
    });

    if (hasInvalidQuantity) {
      errorToast("Quantity cannot be zero");
    } else if (hasMissingVendor) {
      errorToast("Vendor cannot be empty");
    } else if (missingUOM) {
      errorToast("Please add UOM for all items");
    } else if (unbalanced) {
      errorToast(
        `Allocate exactly ${unbalanced.item_quantity} of ${unbalanced.item_name} across vendors`
      );
    } else if (rows.length > 0) {
      // A row with a single vendor emits one itemData entry as before; a
      // split row emits one entry per vendor allocation. Grouping those
      // entries by (vendor, ship address) below turns them into one or more
      // "documents" — each document becomes exactly one purchaseCreateAction
      // (the same single-PO "add" call the regular PO screen uses) in the
      // create loop. Fields the Bulk PO screen doesn't collect (tax, MRP,
      // batch, dates) are defaulted the way a zero-tax, today-dated order
      // would be — this screen has no UI for any of them.
      const itemData = rows.flatMap((item) =>
        getRowAllocations(item)
          .filter((a) => Number(a.qty) > 0)
          .map((a) => ({
            item_name: item?.item_name ?? "",
            item_id: item?.item_id,
            item_generic_name: item?.item_generic_name ?? "",
            molecule_name: "",
            mrp: 0,
            item_baby_box_packing: item?.item_baby_box_packing || "",
            item_batch_total_base_purchase_rate: 0,
            item_total_tax_percentage: 0,
            item_tax_amount: 0,
            item_discount_amount: 0,
            item_discount_percentage: 0,
            item_code: item?.item_code ?? "",
            item_manufacturer_name: item?.item_manufacturer_name ?? "",
            item_uom: item?.item_uom ?? "--",
            item_quantity: Number(a.qty),
            item_unit_price: parseFloat(item?.item_unit_price) || 0,
            item_total_amount: (parseFloat(item?.item_unit_price) || 0) * Number(a.qty),
            item_minimun_purchasing: item?.item_minimun_purchasing
              ? parseInt(item?.item_minimun_purchasing)
              : "",
            item_maximum_purchasing: item?.item_maximum_purchasing
              ? parseInt(item?.item_maximum_purchasing)
              : "",
            item_gst: 0,
            item_sgst: 0,
            item_cgst: 0,
            item_igst: 0,
            item_uom_id: item?.item_uom_id ?? "",
            item_hsn: item?.item_hsn ?? "",
            cmr_code: a.cmr_code,
            cmr_name: a.cmr_name,
            cmr_id: a.cmr_id,
            cmr_ship_to_address: a.cmr_ship_to_address || null,
            cmr_pay_to_address: a.cmr_pay_to_address || null,
          }))
      );

      const documents = groupItemDataByVendorShip(itemData);
      if (documents.length === 0) {
        errorToast("Nothing to create");
        return;
      }
      isCreatingRef.current = true;
      createPurchaseOrderDocuments(documents);
    }
  };

  // The end of a create run. The "already created" memory only ever covers a
  // single submission, so it is dropped once every document is through.
  const finishPoCreation = (message) => {
    createdDocKeysRef.current = new Set();
    isCreatingRef.current = false;
    setPoCreateProgress({ open: false, current: 0, total: 0 });
    successToast(message || "Purchase orders created successfully");
    navigate("/purchase");
  };

  // One purchaseCreateAction call per document, in sequence — the next call
  // only fires after the previous one succeeds, so the progress modal always
  // reflects exactly how many documents have actually been created.
  const createPurchaseOrderDocuments = (documents, index = 0) => {
    // Resuming after a failed run: documents this run already created are
    // stepped over instead of being posted a second time. `index` keeps
    // counting over the FULL document list, so "Adding 3 out of 5" and
    // "Failed to create purchase order 3 of 5" still name the same document
    // they named on the first attempt.
    while (
      index < documents.length &&
      createdDocKeysRef.current.has(buildDocumentKey(documents[index]))
    ) {
      index += 1;
    }
    // Everything in this submission was already created by an earlier attempt
    // — close the run out instead of indexing past the end of the list.
    if (index >= documents.length) {
      finishPoCreation();
      return;
    }
    setPoCreateProgress({ open: true, current: index + 1, total: documents.length });
    const docItems = documents[index];
    const docKey = buildDocumentKey(docItems);
    const today = moment().format("YYYY-MM-DD");
    const payAddress = docItems[0]?.cmr_pay_to_address || null;
    const shipAddress = docItems[0]?.cmr_ship_to_address || null;
    // No live vendor state lookup exists for a manually-picked vendor with no
    // address on file — falls back to "Intra" (no tax difference either way
    // since every tax field below is 0; this only affects which zero goes
    // where cgst/sgst vs igst).
    const vendorState = (
      payAddress?.cmr_pay_address_state ||
      shipAddress?.cmr_ship_address_state ||
      ""
    )
      .toLowerCase()
      .trim();
    const isInterState = Boolean(storeState && vendorState && storeState !== vendorState);
    // cmr_code/cmr_id/cmr_name/cmr_ship_to_address/cmr_pay_to_address on
    // docItems exist only to group rows into documents and to build
    // orderData above — purchase_order_items_list has no vendor/address
    // columns, so they must not be sent as part of an item row itself.
    const itemsForApi = docItems.map((it) => ({
      item_name: it.item_name,
      item_id: it.item_id,
      item_generic_name: it.item_generic_name,
      molecule_name: it.molecule_name,
      mrp: it.mrp,
      item_baby_box_packing: it.item_baby_box_packing,
      item_batch_total_base_purchase_rate: it.item_batch_total_base_purchase_rate,
      item_total_tax_percentage: it.item_total_tax_percentage,
      item_tax_amount: it.item_tax_amount,
      item_discount_amount: it.item_discount_amount,
      item_discount_percentage: it.item_discount_percentage,
      item_code: it.item_code,
      item_manufacturer_name: it.item_manufacturer_name,
      item_uom: it.item_uom,
      item_quantity: it.item_quantity,
      item_unit_price: it.item_unit_price,
      item_total_amount: it.item_total_amount,
      item_minimun_purchasing: it.item_minimun_purchasing,
      item_maximum_purchasing: it.item_maximum_purchasing,
      item_gst: it.item_gst,
      item_sgst: isInterState ? 0 : it.item_sgst,
      item_cgst: isInterState ? 0 : it.item_cgst,
      item_igst: isInterState ? it.item_igst || it.item_tax_amount || 0 : 0,
      item_uom_id: it.item_uom_id,
      item_hsn: it.item_hsn,
    }));
    dispatch(
      purchaseCreateAction(
        {
          orderData: {
            is_draft_order: true,
            store_id: storeId,
            cmr_balance: 0,
            pot_total_gst: 0,
            pot_total_discount: 0,
            pot_payment_status: "paid",
            pot_transaction_id: "",
            pot_billing_address: "",
            pot_sub_total: docItems.reduce((sum, it) => sum + (Number(it.item_total_amount) || 0), 0),
            pot_total_amount: docItems.reduce((sum, it) => sum + (Number(it.item_total_amount) || 0), 0),
            cmr_code: docItems[0]?.cmr_code,
            cmr_id: docItems[0]?.cmr_id,
            cmr_name: docItems[0]?.cmr_name,
            remarks: "",
            pot_order_date: today,
            pot_document_date: today,
            pot_delivery_date: today,
            pot_reference_inv_date: today,
            cmr_gstin: payAddress?.cmr_pay_gst || shipAddress?.cmr_ship_gst || null,
            pot_round_off_value: 0,
            customer_invoice_number: "",
            pot_pay_to_address: payAddress,
            pot_ship_to_address: shipAddress,
            gst_type: isInterState ? "Inter" : "Intra",
            pot_id: null,
            pos_id: null,
          },
          itemData: itemsForApi,
        },
        (data) => {
          // Recorded before the next document is dispatched, so a failure on
          // any later document leaves this one out of the retry.
          createdDocKeysRef.current.add(docKey);
          if (index + 1 >= documents.length) {
            finishPoCreation(data?.message);
          } else {
            createPurchaseOrderDocuments(documents, index + 1);
          }
        },
        (err) => {
          // The documents created before this one stay recorded in
          // createdDocKeysRef, so the form unlocks and pressing "Create
          // Purchase Orders" again resumes from this document instead of
          // re-creating the ones that already went through.
          isCreatingRef.current = false;
          setPoCreateProgress({ open: false, current: 0, total: 0 });
          const createdCount = createdDocKeysRef.current.size;
          errorToast(
            `${
              err?.data?.message ||
              `Failed to create purchase order ${index + 1} of ${documents.length}`
            }${
              createdCount > 0
                ? ` — ${createdCount} purchase order(s) were already created; pressing Create Purchase Orders again will only create the remaining ones.`
                : ""
            }`
          );
        }
      )
    );
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialFormValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => {
        const lastIndex = (values?.data?.length || 0) - 1;
        // Every real line (not the trailing blank row) gets the header vendor.
        const applyHeaderVendorToAllRows = (vendor) =>
          setFieldValue(
            "data",
            (values?.data || []).map((row, i) =>
              i === lastIndex || !row?.item_id ? row : applyVendorToRow(row, vendor)
            )
          );
        // Address saved for the header vendor: update the lines still on that
        // vendor alone - a line the user changed to another vendor or split
        // across vendors keeps its own choice.
        const applyHeaderAddressToRows = (vendor) =>
          setFieldValue(
            "data",
            (values?.data || []).map((row, i) => {
              if (i === lastIndex || !row?.item_id) return row;
              const allocs = getRowAllocations(row);
              if (allocs.length !== 1 || allocs[0].cmr_id !== vendor.cmr_id) {
                return row;
              }
              return applyVendorToRow(row, vendor);
            })
          );
        const onHeaderVendorChange = (option) => {
          if (!option?.value) return;
          const vendor = {
            cmr_id: option.value,
            cmr_code: option.cmr_code,
            cmr_name: option.cmr_first_name,
            cmr_ship_to_address: null,
            cmr_pay_to_address: null,
          };
          setHeaderVendorBoth(vendor);
          applyHeaderVendorToAllRows(vendor);
          // A new vendor needs its address picked.
          setFieldValue(`${HEADER_ADDRESS_NAME}_ship_to_address`, null, false);
          setFieldValue(`${HEADER_ADDRESS_NAME}_pay_to_address`, null, false);
          setHeaderAddressOpen(true);
        };
        const headerShip = headerVendor?.cmr_ship_to_address;
        return (
          <Form className="h-full">
            <div className="h-full">
              <div className="flex justify-between first-div flex-wrap bg-[var(--add-billing-head-bg)] border-radius">
                <div className="p-2 flex items-center gap-4 flex-wrap">
                    <AddButton
                      loading={loadMinQtyLoading}
                      onclkFn={() => {
                        loadMinQtyFn();
                      }}
                      text="Load MIN Quantity Items"
                      prefixIcon={false}
                    />
                    <label className="flex items-center gap-2 text-sm fw_600 text-344054 whitespace-nowrap cursor-pointer">
                      <input
                        type="checkbox"
                        className="cursor-pointer accent-[var(--primary)]"
                        checked={vendorPreference === "preferred_vendor"}
                        onChange={(e) =>
                          setVendorPreference(e.target.checked ? "preferred_vendor" : null)
                        }
                      />
                      Preferred Vendor
                    </label>
                    <label className="flex items-center gap-2 text-sm fw_600 text-344054 whitespace-nowrap cursor-pointer">
                      <input
                        type="checkbox"
                        className="cursor-pointer accent-[var(--primary)]"
                        checked={vendorPreference === "bh_vendor"}
                        onChange={(e) =>
                          setVendorPreference(e.target.checked ? "bh_vendor" : null)
                        }
                      />
                      Best Margin Vendor
                    </label>
                    <div className="flex flex-col">
                    <span className="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]">
                      Vendor Name
                    </span>
                    <div className="relative w-[18rem] min-h-[40px] border border-[var(--search-input-border)] bg-[var(--search-input-bg)] border-radius">
                      <SearchAddFeild
                        name="bulk_po_header_vendor"
                        field="cmr_first_name"
                        isLabelReq={false}
                        isRequired={false}
                        errorClass="hidden"
                        defaultSelectClass=""
                        placeholder="Enter Vendor Name"
                        // null (not an empty label) when nothing is picked, so
                        // react-select shows the placeholder.
                        value={
                          headerVendor
                            ? {
                                label: headerVendor.cmr_name || headerVendor.cmr_code,
                                value: headerVendor.cmr_id,
                              }
                            : null
                        }
                        fetchData={fetchHeaderVendorData}
                        onChange={onHeaderVendorChange}
                      />
                      {headerVendor && (
                        // Clearing the header vendor leaves the lines as they are.
                        <div
                          onClick={() => setHeaderVendorBoth(null)}
                          className="absolute cursor-pointer right-3 top-1/2 transform -translate-y-1/2"
                          title="Clear header vendor"
                        >
                          <CloseIcon width={10} height={10} />
                        </div>
                      )}
                    </div>
                    </div>
                    <button
                      type="button"
                      disabled={!headerVendor}
                      onClick={() => {
                        setFieldValue(
                          `${HEADER_ADDRESS_NAME}_ship_to_address`,
                          headerVendor?.cmr_ship_to_address || null,
                          false
                        );
                        setFieldValue(
                          `${HEADER_ADDRESS_NAME}_pay_to_address`,
                          headerVendor?.cmr_pay_to_address || null,
                          false
                        );
                        setHeaderAddressOpen(true);
                      }}
                      title="Vendor address"
                      className="flex items-center gap-2 px-3 h-[40px] self-end rounded-md border border-[var(--search-input-border)] bg-[var(--search-input-bg)] text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <MapPin width={16} height={16} />
                      {headerShip
                        ? headerShip.cmr_ship_address_city ||
                          headerShip.cmr_ship_address_street ||
                          "Address"
                        : "Address"}
                    </button>
                  </div>
                  <div className="flex gap-3 items-center p-2">
                    <button
                      type="button"
                      onClick={() => {
                        // The rows the "already created" memory refers to are
                        // about to be gone — same reasoning as a fresh load.
                        // A load still in flight is dropped too, so it can't
                        // refill the cleared grid.
                        loadIdRef.current += 1;
                        setLoadMinQtyLoading(false);
                        createdDocKeysRef.current = new Set();
                        setFieldValue("data", [initialData]);
                        // Clear all also clears the header vendor (and its
                        // address), so the next load isn't prefilled with it.
                        setHeaderVendorBoth(null);
                        setHeaderAddressOpen(false);
                        setFieldValue(`${HEADER_ADDRESS_NAME}_ship_to_address`, null, false);
                        setFieldValue(`${HEADER_ADDRESS_NAME}_pay_to_address`, null, false);
                      }}
                      className="text-primary underline text-sm fw_400 whitespace-nowrap"
                      tabIndex="-1"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
                <div
                  style={{
                    height: `calc(100% -  ${heightOfFirstDiv}px)`,
                  }}
                >
                  {loadMinQtyLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      {/* overflow-x-auto is spelled out rather than left to
                          .table-container: the table carries a min-width, and
                          this is the one element that turns that overflow into
                          a single left/right scrollbar for header and rows
                          together. */}
                      <div
                        className="table-container overflow-x-auto"
                        style={{
                          height: `calc(100% -  ${heightOfSecondDiv}px)`,
                        }}
                      >
                        <BulkBillingTable
                          initialData={initialData}
                          onAllocate={(rowIndex) => {
                            setAllocateRowIndex(rowIndex);
                            setAllocateOpen(true);
                          }}
                        />
                      </div>
                      <BulkReturnFooter
                        containerClass="mt-auto second-div flex items-center justify-between gap-4 flex-wrap p-4 border-t border-[var(--general-button-border)] bg-[var(--general-button-bg)]"
                        itemsCount={(values?.data || []).slice(0, -1).length}
                        documentsCount={
                          buildPoVendorAllocationGroups((values?.data || []).slice(0, -1)).length
                        }
                        amount={(values?.data || [])
                          .slice(0, -1)
                          .reduce((sum, row) => sum + (Number(row.item_total_amount) || 0), 0)}
                        showDiscount={false}
                        disabled={poCreateProgress.open}
                        createText="Create Purchase Orders"
                        createType="submit"
                        onCancel={() => {
                          if (values?.data?.length > 0) {
                            if (window.confirm("Are you sure")) {
                              navigate("/purchase");
                            } else return;
                          } else navigate("/purchase");
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
              <BulkPoAllocateSidePanel
                open={allocateOpen}
                setOpen={setAllocateOpen}
                rowIndex={allocateRowIndex}
                preferenceLabel={
                  loadedPreference === "preferred_vendor"
                    ? "Preferred Vendor"
                    : "Best Margin Vendor"
                }
              />
              {headerVendor && (
                <AddressSidebar
                  type="vendor"
                  page="add"
                  disabled={false}
                  path=""
                  open={headerAddressOpen}
                  setOpen={setHeaderAddressOpen}
                  id={headerVendor.cmr_id}
                  name={HEADER_ADDRESS_NAME}
                  onSaved={({ payData, shipData }) => {
                    const vendor = {
                      ...headerVendor,
                      cmr_ship_to_address:
                        shipData || headerVendor.cmr_ship_to_address,
                      cmr_pay_to_address:
                        payData || headerVendor.cmr_pay_to_address,
                    };
                    setHeaderVendorBoth(vendor);
                    applyHeaderAddressToRows(vendor);
                  }}
                  handleDraft={() =>
                    errorToast("Add a new address from the vendor's profile")
                  }
                />
              )}
              <BulkPoCreatingModal
                isOpen={poCreateProgress.open}
                current={poCreateProgress.current}
                total={poCreateProgress.total}
              />
            </Form>
          );
      }}
    </Formik>
  );
};

export default BulkPurchaseOrder;
